import { createTheme } from "@mui/material/styles";
import { OVERLAY_SAFE_POPUP_Z_INDEX } from "./utils/overlayMenu";
import { LAYERS } from "./utils/layering";
import { FONT, NEUTRAL, RADIUS, SHADOW, VIOLET } from "./styles/brandTokens";

const theme = createTheme({
  palette: {
    background: {
      default: NEUTRAL.page,
      paper: NEUTRAL.surface,
    },
    divider: NEUTRAL.border,
    primary: {
      main: VIOLET.main,
      light: VIOLET.theo,
      dark: VIOLET.dark,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#0F766E",
      light: "#14B8A6",
      dark: "#115E59",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#4CAF50", // Green for positive metrics
      light: "#81C784",
      dark: "#388E3C",
    },
    warning: {
      main: "#FFB347", // Orange for highlights
      light: "#FFCC80",
      dark: "#F57C00",
    },
    error: {
      main: "#F44336",
      light: "#EF5350",
      dark: "#D32F2F",
    },
    text: {
      primary: NEUTRAL.text,
      secondary: NEUTRAL.textSecondary,
      disabled: "#B3ABC3",
    },
    grey: {
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },
    // Custom colors for specific chart elements
    custom: {
      theoAI: VIOLET.theo,
      chartGreen: "#00C49A",
      chartOrange: "#FFB347",
      chartBrown: "#A97B50",
      chartGray: "#9E9E9E",
      lightBackground: NEUTRAL.page,
      cardShadow: "rgba(15, 23, 42, 0.08)",
      titleGray: NEUTRAL.textSecondary,
    },
  },
  typography: {
    fontFamily: FONT.sans,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    // Dashboard-specific typography variants
    h1: {
      fontSize: "3.5rem", // Large numbers like "26", "89"
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.01em",
    },
    h2: {
      fontSize: "3rem", // Secondary large numbers like "45%"
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontSize: "2rem", // Medium numbers
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: "1.75rem", // Ring percentages "78%", "26%"
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h5: {
      fontSize: "1.5rem", // Labels like "Hours"
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: "1.125rem", // Component headers
      fontWeight: 700,
      lineHeight: 1.4,
      color: NEUTRAL.text,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none", // Disable uppercase transformation
      letterSpacing: "0",
    },
  },
  zIndex: {
    appBar: LAYERS.appBar,
    drawer: LAYERS.drawer,
    modal: LAYERS.modal,
    snackbar: LAYERS.snackbar,
    tooltip: LAYERS.tooltip,
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
  },
  // Unitless `sx={{ borderRadius: n }}` resolves to n * this. Dropping 4 -> 3
  // pulls the ~320 unitless radii in by a quarter without touching a component.
  shape: {
    borderRadius: 3,
  },
  components: {
    // Card component styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.card,
          boxShadow: SHADOW.card,
          border: `1px solid ${NEUTRAL.border}`,
          transition: "box-shadow 0.2s ease-in-out",
          // Was 0 8px 24px: cards visibly lifted off the page on hover, which
          // reads as playful. Depth now comes from the border, not a halo.
          "&:hover": {
            boxShadow: SHADOW.cardHover,
          },
        },
      },
    },
    // Chip component for badges
    MuiChip: {
      styleOverrides: {
        root: {
          // Full pills on every count and label were the single biggest
          // "bubbly" signal; a small radius keeps them legible as badges.
          borderRadius: RADIUS.control,
          fontWeight: 600,
          fontSize: "0.75rem",
        },
      },
    },
    // Button component styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.control,
          padding: "6px 14px",
          fontWeight: 600,
          boxShadow: "none",
        },
      },
    },
    // IconButton component styling
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.control,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: RADIUS.card,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.control,
          backgroundColor: "#FFFFFF",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: NEUTRAL.borderStrong,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#B9AECD",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: VIOLET.main,
            borderWidth: 2,
          },
        },
      },
    },
    // Global popup layering fix:
    // Keep dropdowns/menus/autocomplete poppers above dialogs and app overlays.
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
        paper: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        disablePortal: false,
        container: typeof document !== "undefined" ? document.body : undefined,
        slotProps: {
          root: {
            sx: {
              zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            },
          },
          paper: {
            sx: {
              zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            },
          },
        },
      },
      styleOverrides: {
        root: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
        paper: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          disablePortal: false,
          container: typeof document !== "undefined" ? document.body : undefined,
          slotProps: {
            root: {
              sx: {
                zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
              },
            },
            paper: {
              sx: {
                zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
              },
            },
          },
          PaperProps: {
            sx: {
              zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            },
          },
          sx: {
            zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
          },
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          popper: {
            sx: {
              zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            },
          },
        },
      },
      styleOverrides: {
        popper: {
          zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
        },
      },
    },
  },
  // Custom breakpoints if needed
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// Add custom color types to theme
declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      theoAI: string;
      chartGreen: string;
      chartOrange: string;
      chartBrown: string;
      chartGray: string;
      lightBackground: string;
      cardShadow: string;
      titleGray: string;
    };
  }

  interface PaletteOptions {
    custom?: {
      theoAI?: string;
      chartGreen?: string;
      chartOrange?: string;
      chartBrown?: string;
      chartGray?: string;
      lightBackground?: string;
      cardShadow?: string;
      titleGray?: string;
    };
  }
}

export default theme;
